import styled, { css, keyframes } from 'styled-components';

/**
 * Viktor's formatted answer inside a bubble.
 *
 * Everything here is tuned for a container a few lines wide: the vertical
 * rhythm is a fraction of what the same elements get on a page, because a
 * bubble's own padding is 7px and document-sized margins would leave a list
 * floating in the middle of it.
 *
 * `$own` follows the rest of the message components and means "sits on a
 * coloured fill" - Viktor's gradient - not "mine". Every tint below is derived
 * from that surface rather than given a colour of its own: translucent white
 * on the gradient, the flat greys on a white card. A fixed colour would be
 * invisible on one of the two.
 */

/**
 * How something arrives while Viktor is writing.
 *
 * Two units, because two different things arrive. Words are what the stream
 * actually delivers - a sentence turns up a few words at a time - and they
 * fade where they stand: a transform on an inline element does nothing, and
 * making each word `inline-block` to allow one would change how the line
 * wraps. A table row or a code block arrives whole, so it gets the small lift
 * as well.
 *
 * Both are short and plain. The reader is mid-sentence; the point is that the
 * next words do not snap into place, not that they make an entrance.
 */
const wordEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const blockEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/**
 * `backwards`, not `both`.
 *
 * Backwards is what makes the element invisible on the very frame it mounts,
 * instead of being painted in place and only then starting to fade - which
 * reads as a flicker rather than as an arrival. Forwards would be pointless
 * and costly: the end state is the element's normal one anyway, and an
 * animation that keeps filling stays alive in the browser's animation list -
 * a long answer would end up dragging hundreds of finished animations behind
 * it.
 *
 * Only while streaming: the stored answer that replaces the streamed one
 * carries the same words, so animating those again would replay a message the
 * reader has already read.
 */
const entrance = (name: ReturnType<typeof keyframes>, duration: string) => css<{
  $animate?: boolean;
}>`
  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${name} ${duration} ease-out backwards;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export const blockEntrance = entrance(blockEnter, '0.35s');

/** One word of the answer, fading in where it lands. */
export const MarkdownWord = styled.span<{ $animate?: boolean }>`
  ${entrance(wordEnter, '0.3s')}
`;

/**
 * The bubble sets `white-space: pre-wrap` so a plain message keeps the line
 * breaks it was typed with. Inside formatted content that is wrong: the
 * newlines between a paragraph and a list are structure, already spent on
 * making the block, and left as-is they print again as blank lines. Only the
 * paragraph below opts back into preserving them.
 */
export const MarkdownRoot = styled.div`
  white-space: normal;

  /* The blocks own the gaps between them; the first and last must not push
     against the bubble's own padding. */
  > *:first-child {
    margin-top: 0;
  }
  > *:last-child {
    margin-bottom: 0;
  }
`;

/** A run of plain lines, with the breaks Viktor wrote still in it. */
export const MarkdownParagraph = styled.p`
  margin: 0 0 8px;
  white-space: pre-wrap;

  /* Contains the floated meta line when this is the block that carries it -
     see the clearfix on Bubble, which this mirrors one level down. */
  ::after {
    content: '';
    display: block;
    clear: both;
  }
`;

export const MarkdownList = styled.ul<{ $ordered?: boolean }>`
  margin: 0 0 8px;
  /* Room for the marker and no more - the browser default is a document-sized
     indent that costs a quarter of the bubble's width. */
  padding-left: 20px;
  list-style: ${({ $ordered }) => ($ordered ? 'decimal' : 'disc')};

  ::after {
    content: '';
    display: block;
    clear: both;
  }
`;

export const MarkdownItem = styled.li`
  margin: 0 0 2px;
  /* A wrapped item lines up under its own text rather than under the marker. */
  padding-left: 2px;

  :last-child {
    margin-bottom: 0;
  }
`;

/**
 * An exact value - a field name, a status, a number to type somewhere.
 */
export const MarkdownCode = styled.code<{ $own?: boolean }>`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  /* Monospace runs visually larger at the same nominal size. */
  font-size: 0.9em;
  padding: 1px 5px;
  border-radius: 5px;
  background: ${({ theme, $own }) => ($own ? `${theme.white}2E` : theme.gray)};
  /* Long identifiers break rather than widening the bubble to the 70% cap. */
  overflow-wrap: anywhere;
`;

/**
 * A fenced block: a query, a payload, a log line. Scrolls sideways rather than
 * wrapping - code that wraps stops being copy-pasteable, and the bubble is
 * already as wide as the thread allows.
 */
export const MarkdownPre = styled.pre<{ $own?: boolean; $animate?: boolean }>`
  ${blockEntrance}
  margin: 0 0 8px;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 18px;
  white-space: pre;
  overflow-x: auto;
  background: ${({ theme, $own }) => ($own ? `${theme.white}2E` : theme.gray)};
`;

/**
 * Keeps a wide table inside the bubble: the table scrolls in here, the bubble
 * stays put. Without this a five-column answer pushes the bubble past the
 * thread's edge and the whole conversation gains a horizontal scrollbar.
 */
export const MarkdownTableWrap = styled.div`
  margin: 0 0 8px;
  max-width: 100%;
  overflow-x: auto;
`;

/**
 * A table arrives a row at a time, so the row is what animates - the wrapper
 * around it is created once, with the header, and would flash the whole table
 * in on the first row.
 */
export const MarkdownRow = styled.tr<{ $animate?: boolean }>`
  ${blockEntrance}
`;

export const MarkdownTable = styled.table<{ $own?: boolean }>`
  border-collapse: collapse;
  font-size: 13px;
  line-height: 18px;

  th,
  td {
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
    border: 1px solid
      ${({ theme, $own }) => ($own ? `${theme.white}4D` : theme.mediumGray)};
  }

  th {
    font-weight: 700;
    background: ${({ theme, $own }) =>
      $own ? `${theme.white}1F` : theme.gray};
  }
`;

export const MarkdownStrong = styled.strong`
  font-weight: 700;
`;

export const MarkdownEm = styled.em`
  font-style: italic;
`;
